import { forEach, isNull, isUndefined, pickBy } from "lodash";
import { COLUMN_FILTER_HANDLERS } from "./filterHandlers";
import TableServiceBase from "./TableServiceBase";

/**
 * TableService extends TableServiceBase with column filtering and query assembly.
 *
 * It adds two flavours of column filters (field/op/term arrays and named "anotherFilters"),
 * permanent filters, and `getFilters()` which assembles the full API query-parameter object.
 *
 * All mutating methods return `this` for chaining. Call `getFilters()` to get the
 * assembled params object ready to pass to an API request.
 */
class TableService extends TableServiceBase {
  // === Column Filters ===

  /**
   * Translates Ant Design column filter values into internal filter state.
   * Delegates to per-type handlers in `filterHandlers.js`.
   *
   * @param {object}   filters - Map of { dataIndex: value } from Ant Design's onChange
   * @param {object[]} columns - Column definitions with `filterType` and `filterNameKey`
   * @returns {TableService}
   */
  manageColumnFilters(filters = {}, columns = []) {
    forEach(filters, (value, columnKey) => {
      const columnProps = columns.find((item) => item.dataIndex === columnKey);
      const handler = COLUMN_FILTER_HANDLERS[columnProps?.filterType];
      if (handler) {
        handler(this, columnKey, value, columnProps);
      }
    });

    return this;
  }

  /**
   * Removes a named filter (anotherFilter) by its nameKey and notifies onChange.
   * @param {string} nameKey
   * @returns {TableService}
   */
  removeAnotherFilter(nameKey) {
    this.anotherFilters = this.anotherFilters.filter((filter) => filter.nameKey !== nameKey);
    this.onFilterChange?.([...this.filters, ...this.anotherFilters]);
    return this;
  }

  /**
   * Clears all active filters (both types), resets the search value, and resets to page 1.
   * @returns {TableService}
   */
  removeAllFilters() {
    this.filters = [];
    this.searchValue = "";
    this.anotherFilters = [];
    this.page = 1;

    return this;
  }

  /**
   * Appends a named filter (serialised as `filters[nameKey]=value`) and triggers onChange.
   * @param {{ nameKey: string, value: *, text?: string, column?: string }} filter
   * @returns {TableService}
   */
  addAnotherFilter({ nameKey, value, text, column }) {
    const filter = { nameKey, value, text, column };
    this.anotherFilters.push(filter);
    this.onFilterChange?.([...this.filters, ...this.anotherFilters]);

    return this;
  }

  /**
   * Appends a field/op/term filter and triggers the onChange callback.
   * Serialised as parallel `field[]`, `op[]`, and `term[]` arrays.
   *
   * @param {{ column: string, op: string, value: *, ...rest }} filter
   * @returns {TableService}
   */
  addFilter({ column, op, value, ...rest }) {
    const filter = { column, op, value, ...rest };
    this.filters.push(filter);
    this.onFilterChange?.([...this.filters, ...this.anotherFilters]);

    return this;
  }

  /**
   * Removes all field/op/term filters for the given column key and notifies onChange.
   * @param {string} columnKey
   * @returns {TableService}
   */
  removeFilter(columnKey) {
    this.filters = this.filters.filter((filter) => filter.column !== columnKey);
    this.onFilterChange?.([...this.filters, ...this.anotherFilters]);
    return this;
  }

  // === Internal Helpers ===

  /**
   * Returns undefined for empty arrays (so pickBy strips them), otherwise
   * returns the array with undefined entries removed.
   * @param {Array} arrayValue
   * @returns {Array|undefined}
   */
  getArrayValues(arrayValue = []) {
    if (arrayValue?.length === 0) {
      return undefined;
    }
    return arrayValue.filter((item) => item !== undefined);
  }

  /**
   * Returns currentValue if truthy, otherwise falls back to defaultValue.
   * @param {*} currentValue
   * @param {*} defaultValue
   * @returns {*}
   */
  applyDefaultParams(currentValue, defaultValue) {
    return currentValue || defaultValue;
  }

  /**
   * Serialises anotherFilters into a flat object of query parameters.
   * Filters in `filterKeyTransformer.keys` use the custom transformer; others use `filters[nameKey]`.
   * @returns {object}
   */
  prepareAnotherFilters() {
    const result = {};
    this.anotherFilters.forEach((item) => {
      const filterKeyTransformer = this.filterKeyTransformer;
      if (filterKeyTransformer?.keys.includes(item.nameKey)) {
        result[filterKeyTransformer.transformer(item)] = item.value;
      } else {
        result[`filters[${item.nameKey}]`] = item.value;
      }
    });
    return result;
  }

  /**
   * Re-applies permanent filters on every getFilters() call so they cannot be overridden.
   */
  applyPermanentFilters() {
    if (this.initialParams?.permanentFilters) {
      this.initialParams.permanentFilters.forEach((item) => {
        this.removeFilter(item.column);
        this.addFilter(item);
      });
    }
  }

  /**
   * Assembles and returns the complete API query-parameter object from current state.
   * Permanent filters are re-applied first; null/undefined/empty values are stripped via pickBy.
   * @returns {object}
   */
  getFilters() {
    this.applyPermanentFilters();

    return pickBy(
      {
        period_start: this.period.start,
        period_end: this.period.end,
        per_page: this.perPage,
        defaultPageSize: this.defaultPageSize,
        page: this.page,
        total: this.total,
        with: this.applyDefaultParams(this.getArrayValues(this.with), this.initialParams.defaultWith),
        order_by_column: this.applyDefaultParams(
          this.getArrayValues(this.orderBy.map((item) => item.column)),
          this.getArrayValues(this.initialParams.defaultOrderBy?.map((item) => item.column))
        ),
        order_by_type: this.applyDefaultParams(
          this.getArrayValues(this.orderBy.map((item) => item.type)),
          this.getArrayValues(this.initialParams.defaultOrderBy?.map((item) => item.type))
        ),
        field: this.getArrayValues(this.filters.map((item) => item.column)),
        op: this.getArrayValues(this.filters.map((item) => item.op)),
        term: this.getArrayValues(this.filters.map((item) => item.value)),
        searchColumns: this.searchValue ? this.searchColumns : undefined,
        searchValue: this.searchValue,
        ...this.params,
        ...this.prepareAnotherFilters()
      },
      (value) => !isNull(value) && !isUndefined(value) && (!!value || value === 0)
    );
  }

  /**
   * Returns a serialisable snapshot of the current filter/sort/search state.
   * Used to persist and restore table state (e.g. to/from localStorage).
   * @returns {object}
   */
  getAllParameters() {
    return {
      params: this.params,
      searchColumns: this.searchColumns,
      searchValue: this.searchValue,
      filters: this.filters,
      anotherFilters: this.anotherFilters,
      orderBy: this.orderBy,
      with: this.with,
      defaultOrderBy: this.initialParams.defaultOrderBy
    };
  }
}

export default TableService;
